// Stellarium Web - Copyright (c) 2022 - Stellarium Labs SRL
//
// This program is licensed under the terms of the GNU AGPL v3, or
// alternatively under a commercial licence.
//
// The terms of the AGPL v3 license can be found in the main directory of this
// repository.

import Vue from 'vue'
import _ from 'lodash'
import StelWebEngine from '@/assets/js/stellarium-web-engine.js'
import Moment from 'moment'

var DDDate = Date
DDDate.prototype.getJD = function () {
  return (this.getTime() / 86400000) + 2440587.5
}

DDDate.prototype.setJD = function (jd) {
  this.setTime((jd - 2440587.5) * 86400000)
}

DDDate.prototype.getMJD = function () {
  return this.getJD() - 2400000.5
}

DDDate.prototype.setMJD = function (mjd) {
  this.setJD(mjd + 2400000.5)
}

const swh = {
  initStelWebEngine: function (store, wasmFile, canvasElem, callBackOnDone) {
    StelWebEngine({
      wasmFile: wasmFile,
      canvas: canvasElem,
      translateFn: function (domain, str) {
        return str
        // return i18next.t(str, {ns: domain});
      },
      onReady: function (lstel) {
        store.commit('replaceStelWebEngine', lstel.getTree())
        lstel.onValueChanged(function (path, value) {
          const tree = store.state.stel
          _.set(tree, path, value)
          store.commit('replaceStelWebEngine', tree)
        })
        Vue.prototype.$stel = lstel
        Vue.prototype.$selectionLayer = lstel.createLayer({ id: 'slayer', z: 50, visible: true })
        Vue.prototype.$observingLayer = lstel.createLayer({ id: 'obslayer', z: 40, visible: true })
        Vue.prototype.$skyHintsLayer = lstel.createLayer({ id: 'skyhintslayer', z: 38, visible: true })
        callBackOnDone()
      }
    })
  },

  monthNames: ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ],

  astroConstants: {
    // Light time for 1 au in s
    ERFA_AULT: 499.004782,
    // Seconds per day
    ERFA_DAYSEC: 86400.0,
    // Days per Julian year
    ERFA_DJY: 365.25,
    // Astronomical unit in m
    ERFA_DAU: 149597870000
  },

  iconForSkySourceTypes: function (skySourceTypes) {
    // Array sorted by specificity, i.e. the most generic names at the end
    const iconForType = {
      // Stars
      'Pec?': 'star',
      '**?': 'double_star',
      '**': 'double_star',
      'V*': 'variable_star',
      'V*?': 'variable_star',
      '*': 'star',

      // Candidates
      'As?': 'group_of_stars',
      'SC?': 'group_of_galaxies',
      'Gr?': 'group_of_galaxies',
      'C?G': 'group_of_galaxies',
      'G?': 'galaxy',

      // Multiple objects
      reg: 'region_defined_in_the_sky',
      SCG: 'group_of_galaxies',
      ClG: 'group_of_galaxies',
      GrG: 'group_of_galaxies',
      IG: 'interacting_galaxy',
      PaG: 'pair_of_galaxies',
      'C?*': 'open_galactic_cluster',
      'Gl?': 'globular_cluster',
      GlC: 'globular_cluster',
      OpC: 'open_galactic_cluster',
      'Cl*': 'open_galactic_cluster',
      'As*': 'group_of_stars',
      mul: 'multiple_objects',

      // Interstellar matter
      'PN?': 'planetary_nebula',
      PN: 'planetary_nebula',
      SNR: 'planetary_nebula',
      'SR?': 'planetary_nebula',
      ISM: 'interstellar_matter',

      // Galaxies
      PoG: 'part_of_galaxy',
      QSO: 'quasar',
      G: 'galaxy',

      dso: 'deep_sky',

      // Solar System
      Asa: 'artificial_satellite',
      Moo: 'moon',
      Sun: 'sun',
      Pla: 'planet',
      DPl: 'planet',
      Com: 'comet',
      MPl: 'minor_planet',
      SSO: 'minor_planet',

      Con: 'constellation'
    }
    for (const i in skySourceTypes) {
      if (skySourceTypes[i] in iconForType) {
        return process.env.BASE_URL + 'images/svg/target_types/' + iconForType[skySourceTypes[i]] + '.svg'
      }
    }
    return process.env.BASE_URL + 'images/svg/target_types/unknown.svg'
  },

  iconForSkySource: function (skySource) {
    return swh.iconForSkySourceTypes(skySource.types)
  },

  iconForObservation: function (obs) {
    if (obs && obs.target) {
      return this.iconForSkySource(obs.target)
    } else {
      return this.iconForSkySourceTypes(['reg'])
    }
  },

  cleanupOneSkySourceName: function (name, flags) {
    name = decodeURIComponent(name)
    name = name.charAt(0).toUpperCase() + name.slice(1)
    flags = flags || 4
    return Vue.prototype.$stel.designationCleanup(name, flags)
  },

  nameForSkySource: function (skySource) {
    if (!skySource || !skySource.names) {
      return '?'
    }
    return this.cleanupOneSkySourceName(skySource.names[0])
  },

  culturalNameToList: function (cn) {
    const res = []

    const formatNative = function (_cn) {
      if (cn.name_native && cn.name_pronounce) {
        return cn.name_native + ', <i>' + cn.name_pronounce + '</i>'
      }
      if (cn.name_native) {
        return cn.name_native
      }
      if (cn.name_pronounce) {
        return cn.name_pronounce
      }
    }

    const nativeName = formatNative(cn)
    if (cn.user_prefer_native && nativeName) {
      res.push(nativeName)
    }
    if (cn.name_translated) {
      res.push(cn.name_translated)
    }
    if (!cn.user_prefer_native && nativeName) {
      res.push(nativeName)
    }
    return res
  },

  namesForSkySource: function (ss, flags) {
    // Return a list of cleaned up names
    if (!ss || !ss.names) {
      return []
    }
    if (!flags) flags = 10
    let res = []
    if (ss.otherNames) {
      if (typeof ss.otherNames === 'string') {
        ss.names = ss.otherNames.split(',').map(name => name.trim())
      }
    }
    res = res.concat(ss.names.map(n => Vue.prototype.$stel.designationCleanup(n, flags)))
    // Remove duplicates, this can happen between * and V* catalogs
    res = res.filter(function (v, i) { return res.indexOf(v) === i })
    res = res.filter(function (v, i) { return !v.startsWith('CON ') })
    return res
  },

  nameForSkySourceType: function (otype) {
    const $stel = Vue.prototype.$stel
    const res = $stel.otypeToStr(otype)
    return res || 'Unknown Type'
  },

  nameForGalaxyMorpho: function (morpho) {
    const galTab = {
      E: 'Elliptical',
      SB: 'Barred Spiral',
      SAB: 'Intermediate Spiral',
      SA: 'Spiral',
      S0: 'Lenticular',
      S: 'Spiral',
      Im: 'Irregular',
      dSph: 'Dwarf Spheroidal',
      dE: 'Dwarf Elliptical'
    }
    for (const morp in galTab) {
      if (morpho.startsWith(morp)) {
        return galTab[morp]
      }
    }
    return ''
  },

  getShareLink: function (context) {
    let link = process.env.VUE_APP_DOMAIN_NAME + '/'
    if (context.$store.state.selectedObject) {
      console.log('selectedObject', context.$store.state.selectedObject);
      return link +  'skysource/hr/' + context.$store.state.selectedObject.hr
    }
    link += '?'
    link += 'fov=' + (context.$store.state.stel.fov * 180 / Math.PI).toPrecision(5)
    const d = new Date()
    d.setMJD(context.$stel.core.observer.utc)
    link += '&date=' + new Moment(d).utc().format()
    link += '&lat=' + (context.$stel.core.observer.latitude * 180 / Math.PI).toFixed(2)
    link += '&lng=' + (context.$stel.core.observer.longitude * 180 / Math.PI).toFixed(2)
    link += '&elev=' + context.$stel.core.observer.elevation
    if (!context.$store.state.selectedObject) {
      link += '&az=' + (context.$stel.core.observer.yaw * 180 / Math.PI).toPrecision(5)
      link += '&alt=' + (context.$stel.core.observer.pitch * 180 / Math.PI).toPrecision(5)
    }
    return link
  },

  // Return a SweObj matching a passed sky source JSON object if it's already instanciated in SWE
  skySource2SweObj: function (ss) {
    if (!ss || !ss.model) {
      return undefined
    }
    const $stel = Vue.prototype.$stel
    let obj
    if (ss.model === 'tle_satellite') {
      const id = 'NORAD ' + ss.model_data.norad_number
      obj = $stel.getObj(id)
    } else if (ss.model === 'constellation' && ss.model_data.iau_abbreviation) {
      const id = 'CON western ' + ss.model_data.iau_abbreviation
      obj = $stel.getObj(id)
    }
    if (!obj) {
      obj = $stel.getObj(ss.names[0])
    }
    if (!obj && ss.names[0].startsWith('Gaia DR2 ')) {
      const gname = ss.names[0].replace(/^Gaia DR2 /, 'GAIA ')
      obj = $stel.getObj(gname)
    }
    if (obj === null) return undefined
    return obj
  },

  lookupSkySourceByName: function (name) {
    return fetch(process.env.VUE_APP_NOCTUASKY_API_SERVER + '/api/v1/skysources/name/' + name)
      .then(function (response) {
        if (!response.ok) {
          throw response.body
        }
        return response.json()
      }, err => {
        throw err.response.body
      })
  },

  querySkySources: function (str, limit) {
    if (!limit) {
      limit = 10
    }
    return fetch(process.env.VUE_APP_NOCTUASKY_API_SERVER + '/api/v1/skysources/?q=' + str + '&limit=' + limit)
      .then(function (response) {
        if (!response.ok) {
          throw response.body
        }
        return response.json()
      }, err => {
        throw err.response.body
      })
  },

  sweObj2SkySource: function (obj) {
    const names = obj.designations()
    const that = this

    if (!names || !names.length) {
      throw new Error("Can't find object without names")
    }

    // Several artifical satellites share the same common name, so we use
    // the unambiguous NORAD number instead
    for (const j in names) {
      if (names[j].startsWith('NORAD ')) {
        const tmpName = names[0]
        names[0] = names[j]
        names[j] = tmpName
      }
    }

    const printErr = function (n) {
      console.log("Couldn't find online skysource data for name: " + n)

      const ss = obj.jsonData
      if (!ss.model_data) {
        ss.model_data = {}
      }
      // Names fixup
      let i
      for (i in ss.names) {
        if (ss.names[i].startsWith('GAIA')) {
          ss.names[i] = ss.names[i].replace(/^GAIA /, 'Gaia DR2 ')
        }
      }
      ss.culturalNames = obj.culturalDesignations()
      return ss
    }

    return that.lookupSkySourceByName(names[0]).then(res => {
      return res
    }, () => {
      if (names.length === 1) return printErr(names)
      return that.lookupSkySourceByName(names[1]).then(res => {
        return res
      }, () => {
        if (names.length === 2) return printErr(names)
        return that.lookupSkySourceByName(names[2]).then(res => {
          return res
        }, () => {
          return printErr(names[2])
        })
      })
    }).then(res => {
      res.culturalNames = obj.culturalDesignations()
      return res
    })
  },

  setSweObjAsSelection: function (obj) {
    const $stel = Vue.prototype.$stel
    $stel.core.selection = obj
    $stel.pointAndLock(obj)
  },

  // Get data for a SkySource from wikipedia
  getSkySourceSummaryFromWikipedia: function (ss) {
    let title
    if (ss.model === 'jpl_sso') {
      title = this.cleanupOneSkySourceName(ss.names[0]).toLowerCase()
      if (['mercury', 'venus', 'earth', 'mars', 'jupiter', 'saturn', 'neptune', 'pluto'].indexOf(title) > -1) {
        title = title + '_(planet)'
      }
      if (ss.types[0] === 'Moo') {
        title = title + '_(moon)'
      }
    }
    if (ss.model === 'mpc_asteroid') {
      title = this.cleanupOneSkySourceName(ss.names[0]).toLowerCase()
    }
    if (ss.model === 'constellation') {
      title = this.cleanupOneSkySourceName(ss.names[0]).toLowerCase() + '_(constellation)'
    }
    if (ss.model === 'dso') {
      for (const i in ss.names) {
        if (ss.names[i].startsWith('M ')) {
          title = 'Messier_' + ss.names[i].substr(2)
          break
        }
        if (ss.names[i].startsWith('NGC ')) {
          title = ss.names[i]
          break
        }
        if (ss.names[i].startsWith('IC ')) {
          title = ss.names[i]
          break
        }
      }
    }
    if (ss.model === 'star') {
      for (const i in ss.names) {
        if (ss.names[i].startsWith('* ')) {
          title = this.cleanupOneSkySourceName(ss.names[i])
          break
        }
      }
    }
    if (!title) return Promise.reject(new Error("Can't find wikipedia compatible name"))

    return fetch('https://en.wikipedia.org/w/api.php?action=query&redirects&prop=extracts&exintro&exlimit=1&exchars=300&format=json&origin=*&titles=' + title,
      { headers: { 'Content-Type': 'application/json; charset=UTF-8' } })
      .then(response => {
        return response.json()
      })
  },

  getGeolocation: function () {
    console.log('Getting geolocalization')

    // First get geoIP location, to use as fallback
    return Vue.jsonp('https://freegeoip.stellarium.org/json/')
      .then(location => {
        var pos = {
          lat: location.latitude,
          lng: location.longitude,
          accuracy: 20000
        }
        console.log('GeoIP localization: ' + JSON.stringify(pos))
        return pos
      }, err => {
        console.log(err)
      }).then(geoipPos => {
        if (navigator.geolocation) {
          return new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(function (position) {
              var pos = {
                lat: position.coords.latitude,
                lng: position.coords.longitude,
                accuracy: position.coords.accuracy
              }
              resolve(pos)
            }, function () {
              console.log('Could not get location from browser, use fallback from GeoIP')
              // No HTML5 Geolocalization support, return geoip fallback values
              if (geoipPos) {
                resolve(geoipPos)
              } else {
                reject(new Error('Cannot detect position'))
              }
            }, { enableHighAccuracy: true })
          })
        }
      })
  },

  delay: function (t, v) {
    return new Promise(function (resolve) {
      setTimeout(resolve.bind(null, v), t)
    })
  },

  geoCodePosition: function (pos, ctx) {
    console.log('Geocoding position... ')
    const ll = ctx.$t('Lat {0}° Lon {1}°', [pos.lat.toFixed(3), pos.lng.toFixed(3)])
    var loc = {
      short_name: pos.accuracy > 500 ? ctx.$t('Near {0}', [ll]) : ll,
      country: 'Unknown',
      lng: pos.lng,
      lat: pos.lat,
      alt: pos.alt ? pos.alt : 0,
      accuracy: pos.accuracy,
      street_address: ''
    }
    return fetch('https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=' + pos.lat + '&lon=' + pos.lng,
      { headers: { 'Content-Type': 'application/json; charset=UTF-8' } }).then(response => {
      if (response.ok) {
        return response.json().then(res => {
          const city = res.address.city ? res.address.city : (res.address.village ? res.address.village : res.name)
          loc.short_name = pos.accuracy > 500 ? ctx.$t('Near {0}', [city]) : city
          loc.country = res.address.country
          if (pos.accuracy < 50) {
            loc.street_address = res.address.road ? res.address.road : res.display_name
          }
          return loc
        })
      } else {
        console.log('Geocoder failed due to: ' + response.statusText)
        return loc
      }
    })
  },

  getDistanceFromLatLonInM: function (lat1, lon1, lat2, lon2) {
    var deg2rad = function (deg) {
      return deg * (Math.PI / 180)
    }
    var R = 6371000 // Radius of the earth in m
    var dLat = deg2rad(lat2 - lat1)
    var dLon = deg2rad(lon2 - lon1)
    var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2)
    var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    var d = R * c // Distance in m
    return d
  },

  // Look for the next time starting from now on when the night Sky is visible
  // i.e. when sun is more than 10 degree below horizon.
  // If no such time was found (e.g. in a northern country in summer),
  // we default to current time.
  getTimeAfterSunset: function (stel) {
    const sun = stel.getObj('NAME Sun')
    const obs = stel.observer.clone()
    const utc = Math.floor(obs.utc * 24 * 60 / 5) / (24 * 60 / 5)
    let i
    for (i = 0; i < 24 * 60 / 5 + 1; i++) {
      obs.utc = utc + 1.0 / (24 * 60) * (i * 5)
      const sunRadec = sun.getInfo('RADEC', obs)
      const azalt = stel.convertFrame(obs, 'ICRF', 'OBSERVED', sunRadec)
      const alt = stel.anpm(stel.c2s(azalt)[1])
      if (alt < -13 * Math.PI / 180) {
        break
      }
    }
    if (i === 0 || i === 24 * 60 / 5 + 1) {
      return stel.observer.utc
    }
    return obs.utc
  },

  // Get the list of circumpolar stars in a given magnitude range
  //
  // Arguments:
  //   obs      - An observer.
  //   maxMag   - The maximum magnitude above which objects are discarded.
  //   filter   - a function called for each object returning false if the
  //              object must be filtered out.
  //
  // Return:
  //   An array SweObject. It is the responsibility of the caller to properly
  //   destroy all the objects of the list when they are not needed, by calling
  //   obj.destroy() on each of them.
  //
  // Example code:
  //   // Return all cicumpolar stars between mag -2 and 4
  //   let res = swh.getCircumpolarStars(this.$stel.observer, -2, 4)
  //   // Do something with the stars
  //   console.log(res.length)
  //   // Destroy the objects (don't forget this line!)
  //   res.map(e => e.destroy())
  getCircumpolarStars: function (obs, minMag, maxMag) {
    const $stel = Vue.prototype.$stel
    const filter = function (obj) {
      if (obj.getInfo('vmag', obs) <= minMag) {
        return false
      }
      const posJNOW = $stel.convertFrame(obs, 'ICRF', 'JNOW', obj.getInfo('radec'))
      const radecJNOW = $stel.c2s(posJNOW)
      const decJNOW = $stel.anpm(radecJNOW[1])
      if (obs.latitude >= 0) {
        return decJNOW >= Math.PI / 2 - obs.latitude
      } else {
        return decJNOW <= -Math.PI / 2 + obs.latitude
      }
    }
    return $stel.core.stars.listObjs(obs, maxMag, filter)
  },

  circumpolarMask: undefined,
  showCircumpolarMask: function (obs, show) {
    if (show === undefined) {
      show = true
    }
    const layer = Vue.prototype.$skyHintsLayer
    const $stel = Vue.prototype.$stel
    if (this.circumpolarMask) {
      layer.remove(this.circumpolarMask)
      this.circumpolarMask = undefined
    }
    if (show) {
      const diam = 2.0 * Math.PI - Math.abs(obs.latitude) * 2
      const shapeParams = {
        pos: [0, 0, obs.latitude > 0 ? -1 : 1, 0],
        frame: $stel.FRAME_JNOW,
        size: [diam, diam],
        color: [0.1, 0.1, 0.1, 0.8],
        border_color: [0.1, 0.1, 0.6, 1]
      }
      this.circumpolarMask = layer.add('circle', shapeParams)
    }
  },
  exportStelObjectsToCSV: function (options = {}) {
    const $stel = Vue.prototype.$stel
    if (!$stel) return null

    const defaults = { maxMag: 20, filename: 'all_stars.csv' }
    const opts = { ...defaults, ...options }
    const obs = $stel.core.observer.clone()
    let objects = []

    try {
      // Ajuster le champ de vision
      console.log('Champ de vision initial (FoV):', $stel.core.fov * 180 / Math.PI)
      
      // Essayer différents niveaux de zoom
      const zoomLevels = [
        Math.PI,           // 180° - très large
        Math.PI / 2,       // 90° - large
        Math.PI / 4,       // 45° - moyen
        Math.PI / 8,       // 22.5° - étroit
        Math.PI / 16,      // 11.25° - très étroit
        Math.PI / 32,      // 5.625° - ultra étroit
        Math.PI / 64       // 2.8125° - extrême
      ]
      
      console.log('Test avec différents niveaux de zoom:')
      zoomLevels.forEach((zoom, index) => {
        console.log(`Zoom ${index + 1}: ${zoom * 180 / Math.PI}°`)
      })
      
      // Commencer avec un zoom moyen
      $stel.core.fov = Math.PI / 8  // 22.5°
      console.log('Champ de vision ajusté (FoV):', $stel.core.fov * 180 / Math.PI)

      // Position de l'observateur
      console.log('Position de l\'observateur:', {
        latitude: obs.latitude * 180 / Math.PI,
        longitude: obs.longitude * 180 / Math.PI,
        utc: obs.utc
      })

      // Méthode 1 : listObjs avec maxMag élevé et différents zooms
      console.log('Méthode 1 : listObjs avec maxMag:', opts.maxMag)
      
      // Tester différents zooms pour voir l'impact
      zoomLevels.forEach((zoom, index) => {
        $stel.core.fov = zoom
        const starsAtZoom = $stel.core.stars.listObjs(obs, opts.maxMag, () => true)
        console.log(`Zoom ${index + 1} (${zoom * 180 / Math.PI}°): ${starsAtZoom ? starsAtZoom.length : 0} étoiles`)
        
        // Chercher Epsilon Aurigae dans cette liste
        if (starsAtZoom) {
          const epsilonAurigae = starsAtZoom.find(star => {
            const designations = star.designations()
            return designations.some(d => 
              d.includes('eps Aur') || 
              d.includes('7 Aur') || 
              d.includes('Epsilon') ||
              d.includes('ε Aur')
            )
          })
          if (epsilonAurigae) {
            console.log(`🎯 Epsilon Aurigae trouvée au zoom ${index + 1} (${zoom * 180 / Math.PI}°)`)
          }
        }
      })
      
      // Utiliser le zoom optimal pour la suite
      $stel.core.fov = Math.PI / 8  // 22.5°
      const starsListObjs = $stel.core.stars.listObjs(obs, opts.maxMag, () => true)
      console.log('Étoiles via listObjs (zoom optimal):', starsListObjs.length)
      console.log('Détails:', starsListObjs.map(s => s.designations()[0]))
      if (starsListObjs) objects = objects.concat(starsListObjs)

      // Méthode 2 : Explorer les propriétés
      console.log('Méthode 2 : Propriétés de $stel.core.stars:', Object.keys($stel.core.stars))

      // Méthode 3 : getObj avec Hipparcos et exploration des propriétés
      console.log('Méthode 3 : Test avec getObj pour étoiles Hipparcos')
      const hipStars = []
      const minHip = 23416;
      const maxHip = 23420; // Couvrir une plus grande plage
      
      // Prendre la première étoile trouvée pour explorer ses propriétés
      let firstStar = null
      for (let i = minHip; i <= maxHip; i++) {
        const star = $stel.getObj(`HIP ${i}`)
        if (star) {          
          hipStars.push(star)
          if (hipStars.length % 1000 === 0) console.log(`Progression HIP: ${hipStars.length} étoiles`)
        }
      }
      console.log('Étoiles HIP trouvées:', hipStars.length)
      console.log('Noms:', hipStars.map(s => s.designations()[0]))
      objects = objects.concat(hipStars)

      // Méthode 4 : Chercher des étoiles brillantes spécifiques
      console.log('Méthode 4 : Chercher des étoiles brillantes spécifiques')
      const brightStars = [
        'HIP 23416', // Epsilon Aurigae (corrigé selon Wikipedia)
        'HIP 24608', // Alpha Aurigae (Capella)
        'HIP 27989', // Beta Aurigae
        'HIP 28360', // Theta Aurigae
        'HIP 23015', // Iota Aurigae
        'HIP 23767', // Eta Aurigae
        'HIP 32349', // Sirius
        'HIP 69673', // Arcturus
        'HIP 97649', // Vega
        'HIP 113368', // Altair
        'HIP 21421', // Aldebaran
        'HIP 24436', // Rigel
        'HIP 30438', // Betelgeuse
        'HIP 65474', // Deneb
        'HIP 80763', // Fomalhaut
        'HIP 37826', // Pollux
        'HIP 36850', // Castor
        'HIP 91262', // Antares
        'HIP 85927', // Spica
      ]
      
      const specificStars = []
      brightStars.forEach(starName => {
        const star = $stel.getObj(starName)
        if (star) {
          specificStars.push(star)
          console.log(`Trouvé: ${starName} - ${star.designations()[0]}`)
        }
      })
      console.log('Étoiles brillantes spécifiques trouvées:', specificStars.length)
      objects = objects.concat(specificStars)

      // Méthode 5 : Chercher par nom d'étoile
      console.log('Méthode 5 : Chercher par nom d\'étoile')
      const starNames = [
        'Epsilon Aurigae',
        'Capella',
        'Sirius',
        'Arcturus',
        'Vega',
        'Altair',
        'Aldebaran',
        'Rigel',
        'Betelgeuse',
        'Deneb',
        'Fomalhaut',
        'Pollux',
        'Castor',
        'Antares',
        'Spica'
      ]
      
      const namedStars = []
      starNames.forEach(starName => {
        const star = $stel.getObj(starName)
        if (star) {
          namedStars.push(star)
          console.log(`Trouvé par nom: ${starName} - ${star.designations()[0]}`)
        } else {
          console.log(`Non trouvé par nom: ${starName}`)
        }
      })
      console.log('Étoiles trouvées par nom:', namedStars.length)
      objects = objects.concat(namedStars)

      // Méthode 6 : Chercher par désignation exacte
      console.log('Méthode 6 : Chercher par désignation exacte')
      const designations = [
        '* eps Aur',
        '* 7 Aur',
        'eps Aur',
        '7 Aur',
        'Epsilon Aurigae',
        'ε Aur'
      ]
      
      const designationStars = []
      designations.forEach(designation => {
        const star = $stel.getObj(designation)
        if (star) {
          designationStars.push(star)
          console.log(`Trouvé par désignation: ${designation} - ${star.designations()[0]}`)
        } else {
          console.log(`Non trouvé par désignation: ${designation}`)
        }
      })
      console.log('Étoiles trouvées par désignation:', designationStars.length)
      objects = objects.concat(designationStars)

      // Méthode 7 : Examiner l'objet sélectionné actuellement
      console.log('Méthode 7 : Examiner l\'objet sélectionné actuellement')
      const currentSelection = $stel.core.selection
      if (currentSelection) {
        console.log('Objet sélectionné actuellement:', currentSelection.designations()[0])
        console.log('Toutes les désignations:', currentSelection.designations())
        console.log('Type d\'objet:', currentSelection.getInfo('otype'))
        console.log('Magnitude:', currentSelection.getInfo('vmag', obs))
        
        // Vérifier si c'est Epsilon Aurigae
        const designations = currentSelection.designations()
        const isEpsilonAurigae = designations.some(d => 
          d.includes('eps Aur') || 
          d.includes('7 Aur') || 
          d.includes('Epsilon') ||
          d.includes('ε Aur')
        )
        
        if (isEpsilonAurigae) {
          console.log('🎯 Epsilon Aurigae détectée dans la sélection actuelle!')
          objects.push(currentSelection)
        }
      } else {
        console.log('Aucun objet sélectionné actuellement')
      }

      // Supprimer les doublons
      objects = [...new Set(objects.map(o => o.designations()[0]))]
        .map(name => objects.find(o => o.designations()[0] === name))

      console.log(`Total d'objets récupérés: ${objects.length}`)

      // Générer CSV
      let csvContent = 'HIP,Nom,Designations,Magnitude,RA (J2000),DEC (J2000),Distance,Cultural Names\n'
      objects.forEach(obj => {
        const names = obj.designations() || ['Inconnu']
        
        // Extraire le numéro HIP
        let hipNumber = ''
        let nom = ''
        const allDesignations = names.join('; ')
        
        // Chercher le numéro HIP dans les désignations
        for (const designation of names) {
          if (designation.startsWith('HIP ')) {
            hipNumber = designation.replace('HIP ', '')
            break
          }
        }
        
        // Utiliser les méthodes du projet pour obtenir les noms
        const allNames = this.namesForSkySource(obj, 26) || []
        
        // Chercher un nom propre (pas HIP, pas HD, etc.)
        if (allNames.length > 0) {
          // Chercher un nom qui n'est pas un catalogue (pas HIP, HD, HR, etc.)
          for (const name of allNames) {
            if (!name.startsWith('HIP ') &&
                !name.startsWith('HD ') &&
                !name.startsWith('HR ') &&
                !name.startsWith('* ') &&
                name.length > 0) {
              nom = name
              break
            }
          }
          // Si aucun nom propre trouvé, utiliser le premier nom
          if (!nom && allNames.length > 0) {
            nom = allNames[0]
          }
        } else {
          // Fallback sur les désignations si namesForSkySource ne fonctionne pas
          for (const designation of names) {
            if (!designation.startsWith('HIP ') && 
                !designation.startsWith('HD ') && 
                !designation.startsWith('HR ') &&
                !designation.startsWith('* ') &&
                designation.length > 0) {
              nom = designation
              break
            }
          }
        }
        
        const mag = obj.getInfo('vmag', obs) ?? 'N/A'
        const radec = obj.getInfo('radec')
        const distance = obj.getInfo('distance', obs) ?? 'N/A'
        
        // Récupérer les noms culturels
        let culturalNames = 'N/A'
        try {
          const cultural = obj.culturalDesignations()
          if (cultural && cultural.length > 0) {
            culturalNames = cultural.join('; ')
          }
        } catch (e) {
          // Ignorer les erreurs
        }
        
        if (radec) {
          const [ra, dec] = $stel.c2s(radec)
          // Convertir RA en heures et DEC en degrés pour correspondre à votre base
          const raHours = ra * 12 / Math.PI  // Conversion radians vers heures
          const decDegrees = dec * 180 / Math.PI  // Conversion radians vers degrés
          
          csvContent += `"${hipNumber}","${nom}","${allDesignations}",${mag},${raHours.toFixed(6)},${decDegrees.toFixed(6)},${distance},"${culturalNames}"\n`
        } else {
          csvContent += `"${hipNumber}","${nom}","${allDesignations}",${mag},N/A,N/A,${distance},"${culturalNames}"\n`
        }
      })

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = opts.filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (e) {
      console.error('Erreur:', e)
    } finally {
      objects.forEach(o => o?.destroy())
      // eslint-disable-next-line no-unused-expressions
      obs?.destroy()
    }
    return objects.length
  },
  luckySearch: function () {
    swh.luckySkySource().then(result => {
      if (result) {
        this.sourceClicked(result)
      }
    }).catch(err => {
      this.autoCompleteChoices = []
      this.searchText = ''
      console.error('Erreur luckySearch:', err)
    })
  },
  luckySkySource: function (options = {}) {
    let url = process.env.VUE_APP_NOCTUASKY_API_SERVER + '/api/v1/for-sale'
    const params = []
    if (options.range) {
      params.push('range=' + encodeURIComponent(options.range))
    }
    if (options.constellation) {
      params.push('constellation=' + encodeURIComponent(options.constellation))
    }
    if (params.length) {
      url += '?' + params.join('&')
    }
    return fetch(url)
      .then(function (response) {
        if (!response.ok) {
          throw response.body
        }
        return response.json()
      }, err => {
        throw err.response?.body || err
      })
  },
  lookupSkySourceByHR: function (hr) {
    return fetch(process.env.VUE_APP_NOCTUASKY_API_SERVER + '/api/v1/skysources/hr/' + hr)
      .then(function (response) {
        if (!response.ok) {
          throw response.body
        }
        return response.json()
      }, err => {
        throw err.response.body
      })
  }
}

export default swh
